import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { BODY_SMALL, HEADING_2 } from "@/components/marketing/marketingType";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { type LaidBlock, useNeighbours } from "../LandingPageContext";
import { LandingImage, LandingSection } from "../LandingPrimitives";
import { SHALLOW_FOOT } from "./landingBands";
import { opensOnNothing, sectionPad } from "./landingRun";
import { Lines } from "@/components/marketing/Lines";

type Stats = Laid<"stats">;

/** `numbers_item_img`: the mark every metric card in the strip carries. */
const METRIC_TICK = "/marketing/landing/metric-tick.svg";

/**
 * `about-numbers_body`: the line under each published figure. It steps twice,
 * a size at each of the site's two upper breaks.
 */
const STAT_LABEL = {
  fontSize: 19,
  fontWeight: 400,
  lineHeight: "28.5px",
  color: marketingPalette.ink,
  [NOT_WIDE]: { fontSize: 17, lineHeight: "25.5px" },
  [MID]: { fontSize: 15, lineHeight: "22.5px" },
} satisfies SxProps<Theme>;

/** `about-numbers_number`: the figure itself, in the band's own deep green. */
const STAT_FIGURE = {
  fontSize: 76,
  lineHeight: "68.4px",
  fontWeight: 400,
  color: marketingPalette.statInk,
  [NOT_WIDE]: { fontSize: 64, lineHeight: "57.6px" },
  [MID]: { fontSize: 48, lineHeight: "43.2px" },
} satisfies SxProps<Theme>;

/**
 * Two published bands with one shape: `metric` is the four-up strip under a
 * product hero, `number` is the tinted "Proxies trusted at scale" band.
 */
export default function StatsBlock({ block }: { block: Stats }) {
  if (block.variant === "number") {
    return <NumberBand block={block} />;
  }
  return <MetricStrip block={block} />;
}

/**
 * `.why-web_item`: the band opens its own section, so it carries the break
 * above it as well as the one below, which the published container does too.
 */
const NUMBER_SECTION = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "32px",
  [MID]: { gap: "24px" },
} satisfies SxProps<Theme>;

/** `.reliable-title_wrap`: chip, heading and lead, on the band's own measure. */
const NUMBER_HEAD = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: "8px",
  width: 1,
  maxWidth: "592px",
} satisfies SxProps<Theme>;

/** `.about-numbers_list.with-padding`: the tinted well the figures sit in. */
const NUMBER_LIST = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "56px",
  width: 1,
  maxWidth: "1152px",
  p: "40px 32px",
  borderRadius: "24px",
  backgroundColor: "rgba(7, 66, 39, 0.08)",
  [MID]: {
    flexDirection: "column",
    gap: "16px",
    p: "16px",
    borderRadius: "16px",
  },
} satisfies SxProps<Theme>;

/**
 * `.about-numbers_item`: each column asks for the whole row and is shrunk back
 * to its share, which is how the published band keeps the three even.
 */
const NUMBER_ITEM = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "4px",
  width: 1,
  [TINY]: { gap: "6.4px" },
} satisfies SxProps<Theme>;

/**
 * A bare `.w-layout-blockcontainer` around the band: 940 wide, 728 below the
 * wide step, the page's width from the card step.
 */
const BLOCK_FRAME = {
  maxWidth: "940px",
  [NOT_WIDE]: { maxWidth: "728px" },
  [MID]: { maxWidth: "none" },
} satisfies SxProps<Theme>;

/** No container around the band: the page's full width, no gutter. */
const BLEED = { maxWidth: "none", px: 0 } satisfies SxProps<Theme>;

/** A second container's gutter inside the first. */
const NESTED = { px: "32px" } satisfies SxProps<Theme>;

function numberFrame(block: Stats) {
  if (block.frame === "block") return BLOCK_FRAME;
  return block.bleed ? BLEED : undefined;
}

function NumberBand({ block }: { block: Stats }) {
  return (
    <LandingSection
      padding={block.pad ? sectionPad(block.pad) : "both"}
      ground={block.ground === "surface" ? "grey" : undefined}
      sx={numberFrame(block)}
    >
      <Box sx={NUMBER_SECTION}>
        <Box sx={NUMBER_HEAD}>
          {block.chip ? <TrustedChip label={block.chip} /> : null}
          {block.title ? (
            <Typography component="h2" sx={HEADING_2}>
              <Lines text={block.title} />
            </Typography>
          ) : null}
          {/* The published band sets its lead a size down whatever the
              registry says, so the tone is not read here. */}
          {block.body ? (
            <Typography sx={BODY_SMALL}>{block.body}</Typography>
          ) : null}
        </Box>
        <Box sx={NUMBER_LIST}>
          {block.items.map((item, index) => (
            <Box key={index} sx={NUMBER_ITEM}>
              <Typography sx={STAT_FIGURE}>{item.value}</Typography>
              <Typography sx={STAT_LABEL}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </LandingSection>
  );
}

/** `.reliable-lbl_wrap`: the pill over the band's heading. */
const TRUSTED_CHIP = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  px: "12px",
  py: "6.08px",
  mb: "4px",
  // A pill at every step, whatever the viewport, until the chip squares off.
  borderRadius: "100vw",
  backgroundColor: marketingPalette.labelChip,
  color: marketingPalette.ink,
  fontSize: 15.04,
  lineHeight: "22.56px",
  [TINY]: {
    px: "8px",
    borderRadius: "16px",
    fontSize: 12.8,
    lineHeight: "19.2px",
  },
} satisfies SxProps<Theme>;

function TrustedChip({ label }: { label: string }) {
  return (
    <Box sx={TRUSTED_CHIP}>
      <TrustedMark />
      <Box component="span">{label}</Box>
    </Box>
  );
}

/**
 * `.reliable-lbl_icon`: the outlined map the published chip draws, in the ink
 * it inherits. Drawn rather than fetched: the published mark is inline SVG.
 */
function TrustedMark() {
  return (
    <Box
      component="svg"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden
      sx={{ flex: "0 0 auto", width: "18px", height: "18px" }}
    >
      <path
        d="M2.25 5.25L6.75 3L11.25 5.25L15.75 3V12.75L11.25 15L6.75 12.75L2.25 15V5.25Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.75 3V12.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.25 5.25V15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
}

/**
 * `.features_numbers_wrap`: the strip hangs 48 under the hero's box, which
 * closes on itself, and 32 from the card step.
 */
const STRIP_HEAD = {
  pt: "48px",
  [MID]: { pt: "32px" },
} satisfies SxProps<Theme>;

/**
 * What the strip closes on. The published section is `.section_static` with
 * no break of its own, so the break under it is whatever the next section
 * carries: nothing where the boxed split brings its own 112 or a pricing band
 * pads its own top, the benefits band's 100, and, over the locations tile,
 * which carries none, the strip's own shallow foot.
 */
function stripFoot(next: LaidBlock | undefined) {
  if (next?.kind === "split") {
    return {};
  }
  if (next?.kind === "plans" && (next.pad === "top" || next.pad === "both")) {
    return {};
  }
  if (opensOnNothing(next)) {
    return SHALLOW_FOOT;
  }
  return undefined;
}

/**
 * The four-up strip under a product hero. Every published strip is figures
 * alone, so no heading is drawn: the strip is the grid and its breaks.
 */
function MetricStrip({ block }: { block: Stats }) {
  const { next } = useNeighbours(block);
  const foot = stripFoot(next);
  return (
    <LandingSection
      {...(block.pad
        ? { padding: sectionPad(block.pad) }
        : {
            padding: foot === undefined ? "bottom" : "none",
            sx: [STRIP_HEAD, foot ?? null, block.nestedFrame ? NESTED : null],
          })}
    >
      <Box
        sx={{
          display: "grid",
          gap: "20px",
          gridTemplateColumns: `repeat(${block.items.length}, minmax(0, 1fr))`,
          [NOT_WIDE]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
          // Two abreast on a phone, as published; only the gutter steps.
          [TINY]: { gap: "11.2px" },
        }}
      >
        {block.items.map((item, index) => (
          <MetricCard
            key={index}
            label={item.label}
            value={item.value}
            large={block.largeFigures}
          />
        ))}
      </Box>
    </LandingSection>
  );
}

const METRIC_CARD = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  p: "24px",
  borderRadius: "16px",
  backgroundColor: marketingPalette.metricTint,
  [TINY]: { gap: "9.6px", p: "11.2px", borderRadius: "9.6px" },
} satisfies SxProps<Theme>;

/** `.h4_new`, where a strip sets its figures as headings rather than `.feature-metric_title`. */
const LARGE_FIGURE = {
  fontSize: 24,
  lineHeight: "28.8px",
  [TINY]: { fontSize: 19, lineHeight: "22.8px" },
} satisfies SxProps<Theme>;

function MetricCard({
  label,
  value,
  large,
}: {
  label: string;
  value: string;
  large?: boolean;
}) {
  return (
    <Box sx={METRIC_CARD}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          [TINY]: { flexDirection: "column", alignItems: "flex-start" },
        }}
      >
        <LandingImage
          src={METRIC_TICK}
          sx={{
            width: "18px",
            height: "18px",
            [TINY]: { width: "20px", height: "20px" },
          }}
        />
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: "19.6px",
            color: marketingPalette.inkSubtle,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        component="h4"
        sx={[
          {
            fontSize: 20,
            lineHeight: "22px",
            fontWeight: 500,
            letterSpacing: "-0.03em",
            color: marketingPalette.ink,
            [TINY]: { fontSize: 17, lineHeight: "18.7px" },
          },
          large ? LARGE_FIGURE : null,
        ]}
      >
        {value}
      </Typography>
    </Box>
  );
}
